/**
 * @fileoverview RTK Query bindings for the `/discover` surface (WEB-53 · M7.3).
 *
 * Both hooks consume the M7.2 endpoint `GET /api/v1/events/discover`:
 *
 * - `useDiscoverEventsQuery` — full payload (events + facets + meta) for the
 *   list, map, and calendar views.
 * - `useDiscoverFacetsQuery` — facet-rail subscribers. **Historically** this
 *   fired a separate `limit=0` request so the backend would short-circuit row
 *   materialization. API-1416 (2026-05-15) collapsed that into a thin
 *   `selectFromResult` selector over `useDiscoverEventsQuery` because the
 *   backend already returns `facets` inline on every `/events/discover`
 *   response, and the two probe requests on cold load were burning the same
 *   60 / 15-min anon-IP rate-limit cap as the real list call.
 *
 *   With the selector in place, a facet-rail subscriber and a list-view
 *   subscriber that pass identical args share a single cache entry and
 *   therefore a single network request. Subscribers passing different args
 *   (e.g. a facets caller that omits `view`/`sort` while the list view
 *   includes them) still fire independent requests, but every request now
 *   pulls real events + facets in one round-trip — no `limit=0` probes.
 *
 * The args type mirrors `DiscoverParams` from M1.1 (multi-select arrays,
 * `price` as a `min-max` / `min+` string) and adds optional location +
 * pagination scalars. We translate to the wire format inside `query()` so the
 * cache key serialization is stable regardless of the caller's array order.
 *
 * @since WEB-53 (Discover · Tri-View Template — M7.3)
 */
import type { ThunkDispatch, UnknownAction } from '@reduxjs/toolkit';
import type { FetchBaseQueryError, FetchBaseQueryMeta } from '@reduxjs/toolkit/query/react';

import type {
  DiscoverAuthenticatedEnvelope,
  DiscoverEvent,
  DiscoverFacetCounts,
  DiscoverMeta,
} from '@loci/types';
import { lociApi } from '../lociApi.js';

// API-1671 — `DiscoverMeta` moved to `@loci/types` so the backend response
// builder and this RTK Query binding share one canonical `meta` contract
// (previously hand-duplicated here, drifting from the OpenAPI spec —
// `requestedWindow` / `servedWindow` were missing). Re-exported so existing
// `import type { DiscoverMeta } from '@loci/api'` call sites keep working.
export type { DiscoverMeta };

/**
 * Sort modes accepted by `/api/v1/events/discover`. Mirrors the web `SortMode`
 * union in `apps/web/lib/discover/url-state.ts` and `discoverSortSchema` in
 * `@loci/validation`.
 *
 * API-1821 (ruling R1): `recommended` and `popularity` are gone; the backend
 * answers them with a 400. Every remaining mode pages by keyset cursor.
 */
export type DiscoverSortMode = 'date' | 'distance' | 'price';

/** View modes accepted by `/api/v1/events/discover` (`calendar` removed in API-1817). */
export type DiscoverViewMode = 'list' | 'map';

/**
 * Args for both `useDiscoverEventsQuery` and `useDiscoverFacetsQuery`.
 *
 * Multi-select fields are accepted as arrays (matching the M1.1 URL state
 * shape) and serialized as comma-joined strings on the wire. Empty arrays are
 * omitted entirely so cache keys for "no filter" requests collapse to the
 * same entry.
 */
export interface DiscoverEventsQueryArgs {
  /** Category slugs (e.g. `['music', 'art']`). */
  category?: string[];
  /** Neighborhood slugs (e.g. `['noda', 'uptown']`). */
  neighborhood?: string[];
  /** Time-of-day buckets (`morning` | `afternoon` | `evening` | `night`). */
  time?: string[];
  /** Free-form vibe / tag filter. */
  vibe?: string[];
  /**
   * WEB-832 — source-published genre filter KEYS (`['hip-hop-rap', 'rock']`),
   * taken from a prior response's `facets.genres[].key`. NOT the display
   * label: `Event.sourceGenre` stores the source's own casing
   * (`'Hip-Hop/Rap'`), and the backend resolves keys back to stored labels.
   *
   * Serialized comma-joined and ONLY when non-empty (see
   * `buildDiscoverWireParams`), so an unfiltered cold load emits no param and
   * the args object stays byte-identical to the pre-WEB-832 shape — the
   * single-fetch invariant.
   *
   * Omitting it applies no genre predicate at all, so events with a NULL
   * `sourceGenre` (every non-Ticketmaster lane, NULL by design) stay in the
   * response. That is the contract the filter is built around.
   */
  genre?: string[];
  /** Followed-venue ids to filter on (FRE-1145). */
  venue?: string[];
  /**
   * Server-resolved follow-graph filter (API-1664, consumed by WEB-662 /
   * DPL-12). When `true` the backend narrows both the events list AND the facet
   * aggregations to events whose venue is followed by the authenticated viewer
   * OR whose lineup includes a followed artist. The server resolves the follow
   * graph itself from the session cookie, so no ids are sent from the client —
   * unlike {@link DiscoverEventsQueryArgs.venue}, which is a client-supplied id
   * list the backend merely intersects.
   *
   * Serialized as the string `'true'` and ONLY when set (see
   * `buildDiscoverWireParams`); `false` / `undefined` emit no param at all, so
   * an unfiltered cold load stays byte-identical to the pre-WEB-662 wire shape
   * and the single-fetch invariant holds.
   *
   * Anonymous callers may set it — the backend documents it as a no-op without
   * a session (`routes/versions/v1/events/discover.ts`, the
   * `followedOnlyViewerId` branch) — but the UI hides the control entirely for
   * viewers with an empty follow graph, so in practice only a viewer who
   * follows someone ever sends it.
   */
  followedOnly?: boolean;
  /**
   * Date filter — either a named keyword (e.g. `tonight`, `weekend`) or an
   * ISO range (`YYYY-MM-DD..YYYY-MM-DD`). Passed through verbatim; the
   * backend resolves the meaning.
   */
  date?: string;
  /**
   * Price range string from M1.1 URL state. Supported shapes:
   *   - `"min-max"` (e.g. `"0-25"`) — splits to `priceMin` + `priceMax`.
   *   - `"min+"`   (e.g. `"100+"`) — sends only `priceMin`.
   *
   * Shapes the backend doesn't recognise are dropped silently so a stray URL
   * value never produces a 400. Callers that need precise wire control can
   * pass `priceMin` / `priceMax` directly.
   */
  price?: string;
  /** Explicit minimum price; takes precedence over the parsed `price` range. */
  priceMin?: number;
  /** Explicit maximum price; takes precedence over the parsed `price` range. */
  priceMax?: number;
  /**
   * WEB-1230 (ruling R6) — with a price bound set, also return events that
   * have no listed price (the "+N not listed" toggle). Serialized as `'true'`
   * and ONLY when set, like {@link DiscoverEventsQueryArgs.followedOnly}, so
   * the cold-load wire shape is unchanged.
   */
  includeUnpriced?: boolean;
  /** Free-text query (max 200 chars; enforced server-side). */
  q?: string;
  /** Active view (informational; does not change the response shape). */
  view?: DiscoverViewMode;
  /** Sort order. */
  sort?: DiscoverSortMode;
  /**
   * Lowercase city slug (e.g. `clt`). **Required (API-1821):** every web
   * Discovery request names its city explicitly. Alone, the backend resolves
   * it to the city's centroid + default radius via the `@loci/city-editions`
   * registry; with `lat`/`lng`, that pair is the anchor (the city centroid or
   * a "Search this area" viewport) and `city` still binds the request.
   */
  city: string;
  /** Search-center latitude `[-90, 90]`. Pair with `lng`. */
  lat?: number;
  /** Search-center longitude `[-180, 180]`. Pair with `lat`. */
  lng?: number;
  /** Search radius in km (default `25`, max `100`). Only valid with `lat`/`lng`. */
  radiusKm?: number;
  /** Opaque cursor returned by `meta.cursor` from a prior request. */
  cursor?: string;
  /** Page size (default `50`, max `100`). */
  limit?: number;
  /**
   * Connected viewer id (WEB-450). **Cache-key discriminator only** — it is
   * intentionally NOT serialized onto the wire (`buildDiscoverWireParams`
   * ignores it): the backend resolves identity from the httpOnly session
   * cookie via `optionalAuthentication`, so echoing a client-asserted id in the
   * URL would be a spoofing smell and leak the id into logs/CDN keys.
   *
   * Its sole job is to give authenticated and anonymous queries **separate RTK
   * Query cache entries**. Because RTK Query's default `serializeQueryArgs`
   * hashes the whole args object, a field the wire builder skips still
   * participates in the cache key. This is what lets a connected viewer's
   * cold-load subscription intentionally miss the anonymous SSR seed and fire
   * exactly one personalized fetch (cookies attached), while anonymous viewers
   * keep reading the seed with zero fetches. Omit for anonymous callers.
   */
  userId?: string;
}

/**
 * Single chip rendered in a `DayCell` on the `/discover` calendar tri-view.
 * The producer caps the per-day array to 3 entries; consumers should still
 * defensively `slice(0, 3)` to defend against future protocol drift.
 */
export interface DiscoverCalendarTopEvent {
  /** URL-safe slug, suitable for the per-event detail link. */
  slug: string;
  /** Display title rendered as the chip label. */
  title: string;
}

/**
 * Top-events-per-day map keyed by local-date `YYYY-MM-DD` (matches
 * `facets.calendar` keys). Days with no matching events may be omitted.
 */
export type DiscoverCalendarTopEvents = Record<string, DiscoverCalendarTopEvent[]>;

/**
 * Full response from `useDiscoverEventsQuery`.
 *
 * FRE-928 — for authenticated viewers, each `DiscoverEvent` may carry an
 * optional `whyReasons` array (≤3 server-rendered, length-capped explainability
 * chips, ordered most-relevant first). The field is omitted entirely for
 * anonymous responses and for events with no qualifying reason — consumers must
 * treat `undefined` as "no reasons", never assume an empty array.
 */
export interface DiscoverEventsResponse {
  events: DiscoverEvent[];
  facets: DiscoverFacetCounts;
  /** Per-day top events powering the calendar tri-view chips (M5.3). */
  calendarTopEvents: DiscoverCalendarTopEvents;
  meta: DiscoverMeta;
  /**
   * FRE-896 — authenticated-only personalization envelope (taste profile,
   * followed artists/venues, saved searches, picked-for-you). Omitted entirely
   * for anonymous responses. Does not affect the RTK Query cache key.
   */
  authenticatedEnvelope?: DiscoverAuthenticatedEnvelope;
}

/** Facet-only response from `useDiscoverFacetsQuery` (events stripped). */
export interface DiscoverFacetsResponse {
  facets: DiscoverFacetCounts;
  /** Per-day top events powering the calendar tri-view chips (M5.3). */
  calendarTopEvents: DiscoverCalendarTopEvents;
  meta: DiscoverMeta;
}

const KEEP_UNUSED_DATA_FOR_SECONDS = 60;
const MULTI_SELECT_KEYS = [
  'category',
  'neighborhood',
  'time',
  'vibe',
  'venue',
  // WEB-832 — genre keys. Empty arrays are omitted by the shared serializer,
  // so no-genre-filter emits no `genre=` param (single-fetch invariant).
  'genre',
] as const;

/** HTTP status the backend rate limiter (`express-rate-limit`) returns. */
const HTTP_TOO_MANY_REQUESTS = 429;

/**
 * The `Retry-After` value the downstream parser reads. We surface the raw
 * header string (RFC 9110 §10.2.3: integer-seconds *or* an HTTP-date) under a
 * dedicated key on the error `data` so the discover error decoder
 * (`apps/web/lib/discover/onDiscoverError.ts`) — which only sees the RTK
 * Query error object, never the `Response` — can compute the countdown.
 *
 * Threading note: the default `fetchBaseQuery` collapses an HTTP error to
 * `{ status, data }` and **drops the `Response` headers entirely**. The
 * `Retry-After` header is therefore invisible at the parse point unless we
 * fold it back in here, where `transformErrorResponse` still has the raw
 * `meta.response`. This is the seam the WEB-289 acceptance criteria depend on.
 */
export const RETRY_AFTER_DATA_KEY = '__retryAfterHeader' as const;

/**
 * Shape of the error `data` after we fold the `Retry-After` header in. The
 * spread preserves whatever JSON body the backend returned (e.g. the
 * `{ error, code, message }` envelope) so existing 4xx message extraction
 * keeps working unchanged.
 */
export interface RateLimitedErrorData {
  readonly [RETRY_AFTER_DATA_KEY]: string | null;
  readonly [key: string]: unknown;
}

/**
 * Fold the `Retry-After` response header into the error `data` on a 429 so
 * the pure discover error decoder can read it. Non-429 errors pass through
 * untouched. `meta.response` is absent on transport-level failures
 * (FETCH_ERROR / TIMEOUT_ERROR) — those never carry a `Retry-After` anyway,
 * so we leave them alone.
 */
function foldRetryAfterHeader(
  error: FetchBaseQueryError,
  meta: FetchBaseQueryMeta | undefined,
): FetchBaseQueryError {
  if (typeof error.status !== 'number' || error.status !== HTTP_TOO_MANY_REQUESTS) {
    return error;
  }
  const headerValue = meta?.response?.headers.get('Retry-After') ?? null;
  const baseData =
    typeof error.data === 'object' && error.data !== null
      ? (error.data as Record<string, unknown>)
      : {};
  const data: RateLimitedErrorData = {
    ...baseData,
    [RETRY_AFTER_DATA_KEY]: headerValue,
  };
  return { status: error.status, data };
}

type DiscoverWireParam = string | number;

/**
 * Dedupe + alpha-sort a multi-select array so cache keys are stable across
 * caller-side ordering differences (`['music','art']` and `['art','music']`
 * produce the same wire string `"art,music"`).
 */
function normalizeMultiSelect(values: readonly string[]): string {
  const cleaned = values.map((v) => v.trim()).filter((v) => v.length > 0);
  return Array.from(new Set(cleaned)).sort().join(',');
}

/**
 * Parse the M1.1 `price` string range. Returns the parsed bounds or `null`
 * for unrecognized shapes — the caller drops `null` results so a malformed
 * URL value doesn't produce a 400.
 */
function parsePriceRange(price: string): { priceMin?: number; priceMax?: number } | null {
  if (price.endsWith('+')) {
    const min = Number.parseFloat(price.slice(0, -1));
    return Number.isFinite(min) && min >= 0 ? { priceMin: min } : null;
  }
  const dashIndex = price.indexOf('-');
  if (dashIndex <= 0) return null;
  const min = Number.parseFloat(price.slice(0, dashIndex));
  const max = Number.parseFloat(price.slice(dashIndex + 1));
  if (!Number.isFinite(min) || !Number.isFinite(max) || min < 0 || max < 0) return null;
  return { priceMin: min, priceMax: max };
}

/**
 * Translate hook args to the wire-format query string. Always returns the
 * same key order for a given input so RTK Query's structural cache key
 * serialization stays deterministic across callers.
 */
function buildDiscoverWireParams(args: DiscoverEventsQueryArgs): Record<string, DiscoverWireParam> {
  const params: Record<string, DiscoverWireParam> = {};

  params.city = args.city;
  if (args.lat !== undefined) params.lat = args.lat;
  if (args.lng !== undefined) params.lng = args.lng;
  if (args.radiusKm !== undefined) params.radiusKm = args.radiusKm;

  for (const key of MULTI_SELECT_KEYS) {
    const arr = args[key];
    if (arr && arr.length > 0) {
      const joined = normalizeMultiSelect(arr);
      if (joined.length > 0) params[key] = joined;
    }
  }

  if (args.date && args.date.length > 0) params.date = args.date;
  if (args.q && args.q.length > 0) params.q = args.q;
  if (args.view) params.view = args.view;
  if (args.sort) params.sort = args.sort;
  if (args.cursor && args.cursor.length > 0) params.cursor = args.cursor;
  if (args.limit !== undefined) params.limit = args.limit;
  // WEB-662 / API-1664: emit ONLY the truthy case. `strictBooleanQuery` would
  // happily accept `followedOnly=false`, but emitting it would change the wire
  // shape (and therefore the request URL) for every unfiltered cold load, which
  // is exactly what the single-fetch invariant forbids. "Off" is the absence of
  // the param, matching how `vibe` / `venue` / `price` express their cleared
  // state.
  if (args.followedOnly === true) params.followedOnly = 'true';
  if (args.includeUnpriced === true) params.includeUnpriced = 'true';

  // Explicit priceMin/priceMax win over the parsed `price` string. Otherwise
  // try to parse `price`; drop silently on unrecognized shapes.
  if (args.priceMin !== undefined) params.priceMin = args.priceMin;
  if (args.priceMax !== undefined) params.priceMax = args.priceMax;
  if (args.priceMin === undefined && args.priceMax === undefined && args.price) {
    const parsed = parsePriceRange(args.price);
    if (parsed) {
      if (parsed.priceMin !== undefined) params.priceMin = parsed.priceMin;
      if (parsed.priceMax !== undefined) params.priceMax = parsed.priceMax;
    }
  }

  return params;
}

// `injectEndpoints` mutates the parent api's runtime endpoint registry, but
// TypeScript only sees the new endpoints on the returned reference. Consumers
// importing from `@loci/api` interact with the hooks below; tests use the
// reference exposed via the `__testing` export to dispatch via
// `endpoints[name].initiate(...)`.
//
// API-1416 (2026-05-15): the previous `discoverFacets` sibling endpoint that
// forced `limit=0` on the wire was removed. `useDiscoverFacetsQuery` is now a
// thin selector wrapper over `discoverEvents` (see below), so callers passing
// identical args share a single network request with the list view instead
// of firing a separate facets probe.
const discoverEndpoints = lociApi.injectEndpoints({
  endpoints: (builder) => ({
    discoverEvents: builder.query<DiscoverEventsResponse, DiscoverEventsQueryArgs>({
      query: (args) => ({
        url: 'v1/events/discover',
        params: buildDiscoverWireParams(args),
      }),
      // WEB-289: on a 429, fold the `Retry-After` header into the error
      // `data` so the discover error decoder can render the countdown. The
      // default `fetchBaseQuery` discards `Response` headers from the error
      // object — this is the only place the raw header is still reachable.
      transformErrorResponse: (
        baseQueryReturnValue: FetchBaseQueryError,
        meta: FetchBaseQueryMeta | undefined,
      ): FetchBaseQueryError => foldRetryAfterHeader(baseQueryReturnValue, meta),
      keepUnusedDataFor: KEEP_UNUSED_DATA_FOR_SECONDS,
      providesTags: [{ type: 'DiscoverEvents', id: 'LIST' }],
    }),
  }),
});

export const { useDiscoverEventsQuery } = discoverEndpoints;

/**
 * Typed `upsertQueryData` for the injected `discoverEvents` endpoint
 * (API-1720). `injectEndpoints` mutates the runtime `lociApi` registry but
 * does not widen its declared TypeScript type, so callers that seed the
 * cache (SSR `DiscoverCacheSeeder`, cold-mount tests) previously cast
 * through `unknown`. Prefer this helper over casting `lociApi.util`.
 *
 * Return type is intentionally a dispatchable `UnknownAction` rather than
 * `ReturnType<typeof discoverEndpoints.util.upsertQueryData>` — naming the
 * injectEndpoints-augmented api in an exported signature pulls unexported
 * sibling endpoint types into the declaration emit (TS4023).
 */
export function upsertDiscoverEventsQueryData(
  args: DiscoverEventsQueryArgs,
  data: DiscoverEventsResponse,
): UnknownAction {
  return discoverEndpoints.util.upsertQueryData(
    'discoverEvents',
    args,
    data,
  ) as unknown as UnknownAction;
}

/** Result shape of a `discoverEvents` cache select — status is what tests assert. */
export interface DiscoverEventsQuerySelection {
  status: string;
  data?: DiscoverEventsResponse;
}

/**
 * Typed `endpoints.discoverEvents.select` for the injected discover query
 * (API-1720). Same rationale as {@link upsertDiscoverEventsQueryData}.
 */
export function selectDiscoverEventsQuery(
  args: DiscoverEventsQueryArgs,
): (state: unknown) => DiscoverEventsQuerySelection {
  return discoverEndpoints.endpoints.discoverEvents.select(args) as (
    state: unknown,
  ) => DiscoverEventsQuerySelection;
}

/** Store slice shape `selectInvalidatedBy` reads from. */
type DiscoverRootState = {
  [lociApi.reducerPath]: ReturnType<typeof lociApi.reducer>;
};

/** Dispatch shape supplied by RTK Query's `onQueryStarted` hooks. */
type DiscoverDispatch = ThunkDispatch<DiscoverRootState, unknown, UnknownAction>;

/**
 * Patch every live `discoverEvents` cache entry's response. Exposed (fully
 * typed, not the raw `discoverEndpoints` object — see FRE-1147 declaration-emit
 * note below) for sibling endpoint modules that need to optimistically reflect
 * a change in `authenticatedEnvelope` (e.g. a saved-search create/delete)
 * without refetching `/events/discover` — preserving the cold-load
 * single-fetch invariant. `discoverEvents` is keyed by the full discover args
 * (city/filters/userId/...), so a caller needs to reach every cache entry the
 * UI is currently subscribed to, not just one. Returns undo handles for
 * rollback on failure.
 *
 * Not exported as `discoverEndpoints` directly: TS4023 declaration-emit
 * errors surface because `injectEndpoints`'s return type references sibling
 * endpoint arg/response types defined only in `lociApi.ts` (e.g.
 * `MapChatRequest`) that aren't re-exported from this module. Exposing this
 * narrow, already-typed function sidesteps that without widening the public
 * surface of `discoverEndpoints`.
 */
export function patchDiscoverEventsCache(
  dispatch: DiscoverDispatch,
  recipe: (draft: DiscoverEventsResponse) => void,
): Array<{ undo: () => void }> {
  const patches: Array<{ undo: () => void }> = [];
  dispatch((_dispatch, getState: () => DiscoverRootState) => {
    const invalidated = discoverEndpoints.util.selectInvalidatedBy(getState(), [
      { type: 'DiscoverEvents', id: 'LIST' },
    ]);
    for (const entry of invalidated) {
      if (entry.endpointName !== 'discoverEvents') continue;
      const args = entry.originalArgs as DiscoverEventsQueryArgs;
      const patch = dispatch(
        discoverEndpoints.util.updateQueryData('discoverEvents', args, recipe),
      );
      patches.push(patch);
    }
  });
  return patches;
}

/**
 * Subset of the `useDiscoverEventsQuery` result that a facet-rail caller
 * actually needs. Intentionally narrower than `useDiscoverEventsQuery`'s
 * return so the selector keeps callers honest — anyone reading `events`
 * should be calling `useDiscoverEventsQuery` directly.
 */
function toFacetsResponse(
  envelope: DiscoverEventsResponse | undefined,
): DiscoverFacetsResponse | undefined {
  return envelope === undefined
    ? undefined
    : {
        facets: envelope.facets,
        calendarTopEvents: envelope.calendarTopEvents,
        meta: envelope.meta,
      };
}

export interface UseDiscoverFacetsQueryResult {
  /** Facets + calendarTopEvents + meta, or `undefined` until the first response lands. */
  data: DiscoverFacetsResponse | undefined;
  /**
   * Argument-matched envelope. `data` can still hold the previous args'
   * response while a new date/filter request is in flight; `currentData` is
   * `undefined` until the current args fulfill (WEB-946).
   */
  currentData: DiscoverFacetsResponse | undefined;
  /** True while the underlying request is in flight (initial load only). */
  isLoading: boolean;
  /** True for the initial fetch only (mirrors RTK Query's flag). */
  isFetching: boolean;
  /** True when the underlying request errored. */
  isError: boolean;
  /** Underlying error, if any. Shape matches RTK Query's `error` field. */
  error: unknown;
}

/**
 * Facets-only view onto `useDiscoverEventsQuery`. Implemented as a selector
 * (`selectFromResult`) so a rail subscriber sharing args with the list view
 * collapses onto the same cache entry and the same network request — no
 * separate `limit=0` probe (API-1416). Callers whose args differ from the
 * list view still fire an independent request, but every request now pulls
 * real events + facets in one round-trip.
 *
 * Returns `undefined` data until the first response lands, mirroring RTK
 * Query semantics. Consumers should still defensively check `data?.facets`.
 */
export function useDiscoverFacetsQuery(
  args: DiscoverEventsQueryArgs,
): UseDiscoverFacetsQueryResult {
  return useDiscoverEventsQuery(args, {
    selectFromResult: ({ data, currentData, isLoading, isFetching, isError, error }) => ({
      data: toFacetsResponse(data),
      currentData: toFacetsResponse(currentData),
      isLoading,
      isFetching,
      isError,
      error,
    }),
  });
}

/**
 * Narrow-typed endpoint slice exposed to tests. Mirrors the runtime shape of
 * `discoverEndpoints.endpoints` but uses an explicit type annotation so the
 * declaration emit doesn't walk the full augmented api type (which pulls in
 * unexported sibling endpoint interfaces).
 */
interface DiscoverEndpointSlice<TArg, TResult> {
  name: string;
  keepUnusedDataFor?: number;
  // RTK Query exposes a runtime `initiate` thunk creator; we only need the
  // ability to dispatch it from tests, so a function shape is sufficient.
  initiate: (arg: TArg) => (
    dispatch: unknown,
    getState: unknown,
    extra: unknown,
  ) => Promise<{
    data?: TResult;
    error?: unknown;
    status: 'fulfilled' | 'rejected';
    unwrap: () => Promise<TResult>;
  }> & { unwrap: () => Promise<TResult> };
}

interface DiscoverTestingEndpoints {
  discoverEvents: DiscoverEndpointSlice<DiscoverEventsQueryArgs, DiscoverEventsResponse>;
}

// Internal helpers + endpoint reference exposed for unit tests; not part of
// the public API. The runtime endpoint registry on `lociApi` is the same
// object — this reference exists purely for type-level access to the
// injected `discoverEvents` definition. The `discoverFacets` sibling endpoint
// was removed in API-1416 — `useDiscoverFacetsQuery` is now a selector over
// `discoverEvents`.
export const __testing = {
  buildDiscoverWireParams,
  normalizeMultiSelect,
  parsePriceRange,
  foldRetryAfterHeader,
  RETRY_AFTER_DATA_KEY,
  KEEP_UNUSED_DATA_FOR_SECONDS,
  endpoints: discoverEndpoints.endpoints as unknown as DiscoverTestingEndpoints,
};
